import { Text, useCursor } from "@react-three/drei";
import { useEffect, useMemo, useRef, useState } from "react";
import { BufferGeometry, Material, Mesh } from "three";

export function PlanogramMesh({
  vertices,
  faces,
  label,
  onActiveChange,
  active,
  ...props
}: JSX.IntrinsicElements["mesh"] & {
  vertices: number[];
  faces: number[];
  label: string;
  active: boolean;
  onActiveChange?: (active: boolean, isMulti: boolean) => void;
}) {
  const mesh = useRef<Mesh<BufferGeometry, Material | Material[]> | null>(null);
  const geometry = useRef<BufferGeometry | null>(null);
  const verticesArray = useMemo(() => new Float32Array(vertices), [vertices]);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (geometry.current && faces) {
      geometry.current.setIndex(faces);
    }
  }, [faces]);

  const fillColor = active ? "#444" : hovered ? "#444" : "#333";

  useCursor(hovered);

  const labelCenter = useMemo(() => {
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;

    for (let i = 0; i < verticesArray.length; i += 3) {
      minX = Math.min(minX, verticesArray[i]);
      maxX = Math.max(maxX, verticesArray[i]);
      minY = Math.min(minY, verticesArray[i + 1]);
      maxY = Math.max(maxY, verticesArray[i + 1]);
      minZ = Math.min(minZ, verticesArray[i + 2]);
      maxZ = Math.max(maxZ, verticesArray[i + 2]);
    }

    return [(minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2] as const;
  }, [verticesArray]);

  return (
    <group>
      <Text
        color={active ? "white" : "#777"}
        fontSize={0.1}
        fontWeight={active ? 700 : 400}
        position={[labelCenter[0], labelCenter[1] + 10, labelCenter[2]]}
        rotation={[-Math.PI / 2, 0, 0]}
        anchorX="center"
        anchorY="middle"
      >
        {label}
      </Text>
      <mesh
        {...props}
        ref={mesh}
        onClick={(e) => onActiveChange?.(!active, e.shiftKey)}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <bufferGeometry ref={geometry}>
          <bufferAttribute
            attach="attributes-position"
            count={verticesArray.length / 3}
            array={verticesArray}
            itemSize={3}
            normalized={false}
          />
        </bufferGeometry>
        <meshBasicMaterial color={fillColor} />
      </mesh>
    </group>
  );
}
